import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import {
    isUiMotionEnabled,
    resolveUiSpring,
} from "../../shared/motion/uiMotion.tools";

interface ItineraryPanelProps {

    /** Child components to render inside the panel */
    "children": React.ReactNode;

    /** Whether the panel is visible */
    "visible": boolean;

    /** Whether dark overlay palette is active */
    "isDarkUi": boolean;
}

interface ItineraryPanelThemeStyle extends React.CSSProperties {
    "--itinerary-text-primary":   string;
    "--itinerary-text-secondary": string;
    "--itinerary-brand-accent":   string;
    "--itinerary-border-strong":  string;
    "--itinerary-card-bg":        string;
}

/**
 * Bottom sheet panel with spring enter/exit for the itinerary results surface.
 *
 * @param props Itinerary panel props.
 * @returns Animated itinerary panel container.
 */
const ItineraryPanel: React.FC<ItineraryPanelProps> = ( {
    children,
    visible,
    isDarkUi,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const panelStyle: ItineraryPanelThemeStyle = {
        "backgroundColor":            palette.itineraryPanelBackground,
        "color":                      palette.overlayTextPrimary,
        "--itinerary-text-primary":   palette.overlayTextPrimary,
        "--itinerary-text-secondary": palette.overlayTextSecondary,
        "--itinerary-brand-accent":   palette.brandPrimary,
        "--itinerary-border-strong":  palette.overlayBorderStrong,
        "--itinerary-card-bg":        palette.overlayCardBackgroundStrong,
    };

    if ( !animationsEnabled ) {
        return (
            <div
                className={`shadow-top md:rounded-t-2xl w-full backdrop-blur-sm ${ !visible && "hidden" }`}
                id="detailModal"
                style={panelStyle}
            >
                {children}
            </div>
        );
    }

    return (
        <AnimatePresence>
            {visible
                ? (
                    <motion.div
                        animate={{
                            "opacity": 1,
                            "y":       0,
                        }}
                        className="shadow-top md:rounded-t-2xl w-full backdrop-blur-sm"
                        exit={{
                            "opacity":    0,
                            "y":          "100%",
                            "transition": resolveUiSpring( reducedMotion, "sheet" ),
                        }}
                        id="detailModal"
                        initial={{
                            "opacity": 0.92,
                            "y":       "100%",
                        }}
                        style={{
                            ...panelStyle,
                            "willChange": "transform, opacity",
                        }}
                        transition={resolveUiSpring( reducedMotion, "sheet" )}
                    >
                        {children}
                    </motion.div>
                )
                : null}
        </AnimatePresence>
    );
};

export default ItineraryPanel;
